import React, { useState } from 'react';
import { FiPackage, FiChevronDown, FiChevronRight, FiLayers, FiCloud, FiShield, FiMonitor, FiSettings, FiServer } from 'react-icons/fi';
import { IconType } from 'react-icons';

interface ModuleSectionData {
  id: string;
  title: string;
  subtitle: string;
  icon: IconType;
  color: string;
  features: string[];
}

interface ColorStyle {
  bg: string;
  text: string;
  border: string;
  dot: string;
}

const MODULE_SECTIONS: ModuleSectionData[] = [
  {
    id: 'hub',
    title: 'Hub',
    subtitle: 'Engagement, Delivery, and Collaboration',
    icon: FiLayers,
    color: 'blue',
    features: [
      'Projects: create/manage projects, budgets, status, and cost tracking configuration',
      'Per-project cloud cost tracking: tag-based allocation, linked AWS environments, and breakdowns',
      'Milestones: plan, track status (planned/in-progress/completed/at-risk), due dates, and progress',
      'Project assignments: allocate people to projects with roles and timelines',
      'Timesheets: log hours per person per project; reporting and delivery cost rollups',
      'Assessments: draft + submit flows; AI-powered scoring, roadmap, recommendations; PDF report generation',
      'Onboarding assessment flow: specialized signup/join flows and contact capture',
      'User management: create/update/deactivate users; password resets; email verification; JWT auth',
      'Client management: multi-client support, client metadata, client trial upgrades',
      'Partner management: partner profiles and settings; manage partner employees',
      'Jira integration: OAuth connection, project mapping, milestone synchronization (versions/sprints)',
      'Support tickets: create tickets, status tracking, replies, and pending-count visibility',
      'Communications: threads, participant management, message send/receive, message history',
      'Notifications: in-app notifications, preferences, subscriptions, digests, read/unread management',
      'Dashboards: create/publish dashboards, widget configuration, layout persistence',
      'Finance analytics: budget summaries, monthly breakdowns, cost categories, alerts visibility',
      'Funding management: sync and track benefit applications, allocations, and credit codes (Partner Central)',
      'AWS account and environment registration: register client accounts, configure/test cross-account roles, metadata',
      'Task execution history: view background task status, duration, and errors',
      'System monitoring and health checks: platform health endpoints and staff-only monitoring views',
    ],
  },
  {
    id: 'platformops',
    title: 'PlatformOps',
    subtitle: 'FinOps, Governance, and Environment Monitoring',
    icon: FiCloud,
    color: 'purple',
    features: [
      'FinOps metrics: budgets, spend, burn rate, remaining budget, and projections',
      'Cost breakdowns: by service, by account, by month, and by tag (for project chargeback/showback)',
      'Credits tracking (operational): track credits consumption vs. remaining (where supported)',
      'Cost optimization scanning: rightsizing and savings recommendations across core AWS services',
      'Recommendation workflows: view recommendations and track status over time',
      'Pricing data collection: collect/update AWS pricing inputs to support recommendations',
      'Tagging compliance: tagged vs. untagged visibility, compliance rate, coverage reporting',
      'Tagging policies: define required tags and valid values; cost allocation tagging',
      'Guardrails scanning: evaluate resources against policies, detect violations, track severity',
      'Violation management: track over time, resolve, and remediate (manual or automated workflows)',
      'Environment monitoring: register environments, track health checks, uptime trend and status history',
      'Manual credits management: record credits from multiple sources, track expiration and consumption',
    ],
  },
  {
    id: 'cloudops',
    title: 'CloudOps Overview',
    subtitle: 'Unified Ops Snapshot',
    icon: FiMonitor,
    color: 'green',
    features: [
      'Platform overview dashboard: health score, compliance score, active environments, open violations, estimated savings',
    ],
  },
  {
    id: 'webapp',
    title: 'Web Application',
    subtitle: 'User Experience',
    icon: FiSettings,
    color: 'indigo',
    features: [
      'Hub dashboards: customizable widgets, multiple views (hub/genaiops/agentops contexts)',
      'Projects UI: list/detail views, create/edit, budgets, milestones, timesheets, people, cost config, environment linking',
      'Assessment UI: step-by-step flow, conditional questions, progress tracking, results, PDF export, SME booking, contact capture',
      'Finance UI: metrics, spend trend charts, per-project tables, budget alerts, monthly visualization',
      'Integrations UI: Jira connection wizard, mapping configuration, milestone sync visibility',
      'AWS environments UI: account registration, cross-account setup guidance, access tests, listings and details',
      'Access management UI: user and role management, hierarchy views, permission matrix, client management',
      'Admin panel: unified management tables for users, clients, partners, projects, and AWS users',
      'Task monitoring UI: task list, task details, error display, filtering',
      'GenAIOps UI: tracing, prompt management, evaluations, vectors, model costs',
      'AgentOps UI: agent runs, tool usage, safety monitoring, performance metrics, memory management',
      'DataOps UI: catalog, lineage, events, runs, impact, governance',
    ],
  },
  {
    id: 'workers',
    title: 'Background Processing and Automation',
    subtitle: 'Workers',
    icon: FiServer,
    color: 'orange',
    features: [
      'Assessment processing pipeline: scoring, analysis, roadmap and recommendations generation, PDF creation, email notifications',
      'Guardrails jobs: scheduled scans, remediation execution, violation alerting',
      'Cost operations jobs: pricing refresh, optimization scans, budget alerts, anomaly detection framework',
      'GenAIOps automation: scheduled evaluations, evaluation runs, vector exports, alerting and auto-actions',
      'Notification jobs: digest generation and event publishing',
      'Monitoring jobs: platformops monitoring tasks',
    ],
  },
  {
    id: 'security',
    title: 'Security, Access Control, and Tenant Isolation',
    subtitle: '',
    icon: FiShield,
    color: 'red',
    features: [
      'Multi-tenant role-based access control (RBAC) across staff, AWS, partners, and clients',
      'Tenant scoping rules: clients see only their data; partners see their portfolio; staff has global visibility',
      'AWS access model: cross-account IAM role assumption with external ID protection',
      'Data boundaries: cost + metadata access without reading customer application data or secrets',
      'Auditability: actions and workflows support audit trails via system records and notifications',
    ],
  },
];

const colorMap: Record<string, ColorStyle> = {
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
    dot: 'bg-purple-500',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    dot: 'bg-green-500',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-800',
    dot: 'bg-indigo-500',
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    dot: 'bg-orange-500',
  },
  red: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
  },
};

interface ModuleSectionProps {
  section: ModuleSectionData;
}

const ModuleSection: React.FC<ModuleSectionProps> = ({ section }) => {
  const [expanded, setExpanded] = useState(true);
  const colors = colorMap[section.color];
  const Icon = section.icon;

  return (
    <div className={`border ${colors.border} rounded-lg overflow-hidden transition-colors`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white transition-colors">
              {section.title}
            </h3>
            {section.subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">{section.subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full transition-colors">
            {section.features.length} {section.features.length === 1 ? 'feature' : 'features'}
          </span>
          {expanded ? (
            <FiChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <FiChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 transition-colors">
          <ul className="space-y-2.5">
            {section.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2.5">
                <span className={`mt-2 w-1.5 h-1.5 rounded-full ${colors.dot} flex-shrink-0`} />
                <span className="text-sm text-gray-700 dark:text-gray-300 transition-colors">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ChangelogPage: React.FC = () => {
  const totalFeatures = MODULE_SECTIONS.reduce((sum, s) => sum + s.features.length, 0);

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">Changelog</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Release history and feature catalog for Aivilo Lens
        </p>
      </div>

      <div className="mb-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition-colors">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 transition-colors">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <FiPackage className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white transition-colors">
                    Version 1.0.1
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
                    February 2026
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 text-sm font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full transition-colors">
                  Latest
                </span>
                <span className="px-3 py-1 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full transition-colors">
                  {totalFeatures} features
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 transition-colors">
              Feature Catalog by Module
            </h3>
            <div className="space-y-4">
              {MODULE_SECTIONS.map((section) => (
                <ModuleSection key={section.id} section={section} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
